// Card de conquista compartilhável: a foto do progresso da PESSOA (título,
// XP, sequência e pílulas dominadas) num formato 4:5, pensado para story/feed.
// Nada de dado da organização aqui — nem órgão, nem e-mail: o card sai do
// dispositivo para uma rede social, então só entra o que é da pessoa e a marca
// do tenant.
import {useCallback, useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    useMediaQuery,
} from '@mui/material'
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined'
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined'
import {toBlob} from 'html-to-image'
import {getCurrentColors} from '../../../utils/colors'
import {LICITO_IMAGES} from '../../../utils/logos'

const COLORS = getCurrentColors()
// Wordmark do PRODUTO, não o do tenant: getCurrentImages() devolve a marca do
// órgão nos subdomínios white-label, e a imagem sai daqui para uma rede
// social. Estampar o brasão da prefeitura/autarquia entregaria o empregador da
// pessoa — exatamente o que a linha "nada da sua organização" promete não
// fazer.
const WORDMARK = LICITO_IMAGES.logo

// Proporção 4:5 (o retrato que Instagram/LinkedIn não cortam). Medidas fixas
// em px porque é isso que o html-to-image captura — o export não pode depender
// do tamanho da tela de quem compartilha.
const LARGURA = 300
const ALTURA = 375
const ARQUIVO = 'trilha-do-guru.png'

const COR_FOGO = '#E8710A'
const COR_ACERTO = '#2E7D32'

const Palco = styled.div`
    display: flex;
    justify-content: center;
    overflow-x: auto;
    padding: 4px 0 8px;
`

const Cartao = styled.div`
    width: ${LARGURA}px;
    height: ${ALTURA}px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    overflow: hidden;
    background-color: ${COLORS.branco};
    border: 1px solid ${COLORS.cinzaDesbotado};
`

const Faixa = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 14px 20px;
    /* Gradiente primária → secundária, como no protótipo: aqui é marca em
       decoração, nunca semântica — e o PNG exportado carrega o gradiente. */
    background: linear-gradient(
        120deg,
        ${COLORS.primariaEscura},
        ${COLORS.secundariaEscura}
    );
    color: ${COLORS.branco};
`

const Corpo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 18px 20px 16px;
`

const Grade = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: auto;
`

const Stat = styled.div`
    background-color: ${COLORS.cinzaClaro};
    border: 1px solid ${COLORS.cinzaDesbotado};
    border-radius: 12px;
    padding: 10px 6px;
    text-align: center;
`

const Rodape = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid ${COLORS.cinzaDesbotado};
`

const Wordmark = styled.img`
    height: 18px;
    width: auto;
    object-fit: contain;
`

// Navegador sem Web Share de arquivo (desktop em geral) não pode ganhar um
// botão que não faz nada: detecta antes de renderizar. `canShare` só responde
// de verdade com um File na mão, daí o arquivo vazio de sonda.
function suportaCompartilharArquivo(): boolean {
    try {
        if (typeof navigator === 'undefined') return false
        if (typeof navigator.share !== 'function') return false
        if (typeof navigator.canShare !== 'function') return false
        if (typeof File !== 'function') return false
        return navigator.canShare({
            files: [new File([], ARQUIVO, {type: 'image/png'})],
        })
    } catch {
        return false
    }
}

function suportaCopiarImagem(): boolean {
    try {
        if (typeof navigator === 'undefined') return false
        if (typeof navigator.clipboard?.write !== 'function') return false
        return typeof window.ClipboardItem === 'function'
    } catch {
        return false
    }
}

interface Aviso {
    tipo: 'success' | 'error'
    texto: string
}

interface CardConquistaProps {
    aberto: boolean
    aoFechar: () => void
    titulo: string
    xp: number
    streak: number
    recorde: number
    dominadas: number
}

export default function CardConquista({
    aberto,
    aoFechar,
    titulo,
    xp,
    streak,
    recorde,
    dominadas,
}: CardConquistaProps) {
    const telaPequena = useMediaQuery('(max-width:640px)')
    // `| null` explícito: a ref é escrita à mão pela ref de callback, e
    // useRef<T>(null) produz um RefObject de `current` somente-leitura
    const cartaoRef = useRef<HTMLDivElement | null>(null)
    // PNG já gerado (e a geração em curso, para não disparar duas). É o que
    // permite chamar navigator.share dentro do gesto — ver `aquecer`.
    const imagemRef = useRef<Blob | null>(null)
    const geracaoRef = useRef<Promise<Blob> | null>(null)
    const revogacoesRef = useRef<Array<{url: string; timer: number}>>([])
    const [ocupado, setOcupado] = useState(false)
    const [aviso, setAviso] = useState<Aviso | null>(null)
    // Detecção só quando o diálogo abre: em jsdom (e em qualquer navegador sem
    // as APIs) o estado fica falso e os botões nem chegam à tela.
    const [suporte, setSuporte] = useState({compartilhar: false, copiar: false})

    // Uma única geração por abertura: o PNG de 900x1125 custa caro e as três
    // ações usam exatamente a mesma imagem. Falha limpa o cache para que o
    // clique seguinte possa tentar de novo.
    const gerar = useCallback(() => {
        if (!geracaoRef.current) {
            const pendente = (async () => {
                const no = cartaoRef.current
                if (!no) throw new Error('card indisponível')
                const imagem = await toBlob(no, {
                    pixelRatio: 3,
                    cacheBust: true,
                    width: LARGURA,
                    height: ALTURA,
                    backgroundColor: COLORS.branco,
                    // o card pode estar escalado/deslocado na tela; o export é
                    // sempre o retrato limpo
                    style: {transform: 'none', margin: '0'},
                })
                if (!imagem) throw new Error('imagem vazia')
                imagemRef.current = imagem
                return imagem
            })().catch((erro) => {
                geracaoRef.current = null
                throw erro
            })
            geracaoRef.current = pendente
        }
        return geracaoRef.current
    }, [])

    // Pré-geração assim que o card existe no DOM, não no clique. Safari (iOS
    // inclusive) só aceita navigator.share/clipboard.write com ativação
    // transitória do usuário, e ela morre no primeiro await: gerar o PNG
    // (centenas de ms) antes de chamar share derruba a folha de
    // compartilhamento com NotAllowedError justo no único lugar onde o Web
    // Share de arquivo importa. Com a imagem pronta no ref, o handler do
    // clique chega ao share sem nenhum await no caminho.
    // O gatilho é a ref de callback porque o Portal do MUI só monta o conteúdo
    // do diálogo num segundo commit — num useEffect do próprio componente a
    // ref ainda estaria nula. A rejeição é tratada no clique; aqui é só
    // aquecimento, e não pode virar unhandled rejection.
    const aquecer = useCallback(() => {
        void gerar().catch(() => {})
    }, [gerar])

    const definirCartao = useCallback(
        (no: HTMLDivElement | null) => {
            cartaoRef.current = no
            if (no) aquecer()
        },
        [aquecer]
    )

    useEffect(() => {
        if (!aberto) {
            // fechou: solta o PNG (alguns MB) em vez de segurá-lo pela vida
            // toda do componente, que fica montado com o diálogo fechado
            imagemRef.current = null
            geracaoRef.current = null
            return
        }
        setAviso(null)
        setSuporte({
            compartilhar: suportaCompartilharArquivo(),
            copiar: suportaCopiarImagem(),
        })
        // reabriu antes de a transição de saída desmontar o card: a ref de
        // callback não dispara de novo, então o aquecimento sai daqui
        if (cartaoRef.current) aquecer()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto])

    // Rede de segurança das object URLs pendentes (ver `baixar`): o que ainda
    // não venceu o timer é revogado ao desmontar, para o blob não sobreviver à
    // tela.
    useEffect(
        () => () => {
            revogacoesRef.current.forEach(({url, timer}) => {
                window.clearTimeout(timer)
                URL.revokeObjectURL(url)
            })
            revogacoesRef.current = []
        },
        []
    )

    // jsdom não tem canvas e o próprio html-to-image pode falhar (fonte
    // remota, imagem sem CORS): toda ação passa por aqui e vira aviso, nunca
    // exceção solta. A tarefa roda SÍNCRONA até o primeiro await — é o que
    // preserva o gesto do usuário quando a imagem já está pronta.
    const executar = async (
        tarefa: () => Promise<void>,
        mensagemDeErro: string
    ) => {
        if (ocupado) return
        setOcupado(true)
        setAviso(null)
        try {
            await tarefa()
        } catch (erro) {
            // usuário fechou a folha de compartilhamento: não é erro
            if ((erro as {name?: string})?.name === 'AbortError') return
            setAviso({tipo: 'error', texto: mensagemDeErro})
        } finally {
            setOcupado(false)
        }
    }

    const compartilhar = () =>
        executar(async () => {
            // `?? await` não suspende quando a imagem já está no ref: o share
            // sai no mesmo tick do clique (ver comentário da pré-geração).
            const imagem = imagemRef.current ?? (await gerar())
            const arquivo = new File([imagem], ARQUIVO, {type: 'image/png'})
            if (!navigator.canShare?.({files: [arquivo]})) {
                throw new Error('sem suporte a arquivo')
            }
            await navigator.share({
                files: [arquivo],
                title: 'Minha conquista na Trilha do Guru',
                text: `${titulo} · ${xp} XP na Trilha do Guru.`,
            })
        }, 'Não foi possível compartilhar. Baixe o PNG e poste manualmente.')

    const copiar = () =>
        executar(async () => {
            // Sem imagem pronta, o ClipboardItem recebe a PROMESSA: é a forma
            // que o Safari aceita para gerar conteúdo depois do gesto.
            const item = new ClipboardItem({
                'image/png': imagemRef.current ?? gerar(),
            })
            await navigator.clipboard.write([item])
            setAviso({
                tipo: 'success',
                texto: 'Imagem copiada — cole onde quiser.',
            })
        }, 'Não foi possível copiar a imagem. Tente baixar o PNG.')

    const baixar = () =>
        executar(async () => {
            const imagem = imagemRef.current ?? (await gerar())
            if (typeof URL.createObjectURL !== 'function') {
                throw new Error('sem createObjectURL')
            }
            const url = URL.createObjectURL(imagem)
            const link = document.createElement('a')
            link.href = url
            link.download = ARQUIVO
            document.body.appendChild(link)
            link.click()
            link.remove()
            // Firefox cancela o download se a URL for revogada no mesmo tick
            // do click(): revoga depois, com a desmontagem como rede.
            const timer = window.setTimeout(() => {
                URL.revokeObjectURL(url)
                revogacoesRef.current = revogacoesRef.current.filter(
                    (r) => r.url !== url
                )
            }, 60000)
            revogacoesRef.current.push({url, timer})
            setAviso({tipo: 'success', texto: 'PNG salvo no seu dispositivo.'})
        }, 'Não foi possível gerar a imagem. Tente novamente.')

    const acao = {
        textTransform: 'none',
        width: {xs: '100%', sm: 'auto'},
        // alvo de toque confortável no celular (o padrão do MUI é ~36px)
        minHeight: {xs: '44px', sm: 'auto'},
        /* Botão NUNCA encolhe nem corta rótulo: com três ações mais o
           Fechar na mesma fileira, o flexbox espremia o "Compartilhar" até
           o texto vazar por baixo do ícone. Quem cede é a fileira, que
           quebra linha (flexWrap no DialogActions). */
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '&:focus-visible': {
            outline: `2px solid ${COLORS.primariaEscura}`,
            outlineOffset: '2px',
        },
    } as const

    return (
        <Dialog
            open={aberto}
            onClose={aoFechar}
            fullWidth
            maxWidth="xs"
            fullScreen={telaPequena}
            aria-labelledby="titulo-dialog-conquista"
        >
            <DialogTitle id="titulo-dialog-conquista">
                Seu card de conquista
            </DialogTitle>
            <DialogContent sx={{px: {xs: 1.5, sm: 3}}}>
                <Typography color="text.secondary" sx={{mb: 1.5}}>
                    Mostre o seu progresso. A imagem tem só os seus números —
                    nada da sua organização.
                </Typography>

                <Palco>
                    <Cartao ref={definirCartao}>
                        <Faixa>
                            <EmojiEventsOutlinedIcon sx={{fontSize: 22}} />
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    letterSpacing: 2,
                                }}
                            >
                                TRILHA DO GURU
                            </Typography>
                        </Faixa>
                        <Corpo>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{fontSize: '13px', letterSpacing: 1.5}}
                            >
                                MEU TÍTULO
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '30px',
                                    lineHeight: 1.15,
                                    fontWeight: 'bold',
                                    color: COLORS.primariaEscura,
                                }}
                            >
                                {titulo}
                            </Typography>
                            <Box sx={{mt: 1.5}}>
                                <Chip
                                    size="small"
                                    icon={
                                        <BoltOutlinedIcon
                                            sx={{
                                                color: COLORS.secundariaEscura,
                                            }}
                                        />
                                    }
                                    label={`${xp} XP acumulados`}
                                    sx={{fontWeight: 'bold'}}
                                />
                            </Box>

                            <Grade>
                                <Stat>
                                    <LocalFireDepartmentOutlinedIcon
                                        sx={{fontSize: 18, color: COR_FOGO}}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {streak}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11.5px',
                                            lineHeight: 1.3,
                                        }}
                                        color="text.secondary"
                                    >
                                        {streak === 1
                                            ? 'dia seguido'
                                            : 'dias seguidos'}
                                    </Typography>
                                </Stat>
                                <Stat>
                                    <EmojiEventsOutlinedIcon
                                        sx={{
                                            fontSize: 18,
                                            color: COLORS.secundariaEscura,
                                        }}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {recorde}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11.5px',
                                            lineHeight: 1.3,
                                        }}
                                        color="text.secondary"
                                    >
                                        recorde de dias
                                    </Typography>
                                </Stat>
                                <Stat>
                                    <CheckRoundedIcon
                                        sx={{fontSize: 18, color: COR_ACERTO}}
                                    />
                                    <Typography
                                        sx={{
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {dominadas}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '11.5px',
                                            lineHeight: 1.3,
                                        }}
                                        color="text.secondary"
                                    >
                                        {dominadas === 1
                                            ? 'pílula dominada'
                                            : 'pílulas dominadas'}
                                    </Typography>
                                </Stat>
                            </Grade>

                            <Rodape>
                                <Wordmark src={WORDMARK} alt="" />
                                <Typography
                                    sx={{fontSize: '11.5px', lineHeight: 1.3}}
                                    color="text.secondary"
                                >
                                    licito.guru — Trilha do Guru
                                </Typography>
                            </Rodape>
                        </Corpo>
                    </Cartao>
                </Palco>

                {aviso && (
                    <Alert
                        severity={aviso.tipo}
                        sx={{mt: 1.5}}
                        onClose={() => setAviso(null)}
                    >
                        {aviso.texto}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    px: {xs: 1.5, sm: 3},
                    pb: 2,
                    gap: 1,
                    flexDirection: {xs: 'column', sm: 'row'},
                    alignItems: {xs: 'stretch', sm: 'center'},
                    flexWrap: 'wrap',
                    '& > :not(style) ~ :not(style)': {ml: 0},
                }}
            >
                <Button
                    color="inherit"
                    onClick={aoFechar}
                    sx={{...acao, mr: {sm: 'auto'}}}
                >
                    Fechar
                </Button>
                {suporte.compartilhar && (
                    <Button
                        variant="contained"
                        disabled={ocupado}
                        onClick={compartilhar}
                        startIcon={
                            ocupado ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                <IosShareOutlinedIcon />
                            )
                        }
                        sx={{...acao, backgroundColor: COLORS.botaoFormulario}}
                    >
                        Compartilhar
                    </Button>
                )}
                {suporte.copiar && (
                    <Button
                        variant="outlined"
                        color="inherit"
                        disabled={ocupado}
                        onClick={copiar}
                        startIcon={<ContentCopyOutlinedIcon />}
                        sx={acao}
                    >
                        Copiar imagem
                    </Button>
                )}
                <Button
                    variant={suporte.compartilhar ? 'outlined' : 'contained'}
                    color={suporte.compartilhar ? 'inherit' : 'primary'}
                    disabled={ocupado}
                    onClick={baixar}
                    startIcon={<DownloadOutlinedIcon />}
                    sx={
                        suporte.compartilhar
                            ? acao
                            : {...acao, backgroundColor: COLORS.botaoFormulario}
                    }
                >
                    Baixar PNG
                </Button>
            </DialogActions>
        </Dialog>
    )
}
